import React, { useState, useRef, useCallback, useEffect } from 'react';

// Both presentations list the same doctor; only the shell around the row differs.
function DoctorRow({ doctor }) {
  return (
    <React.Fragment>
      {doctor.avatar ? (
        <img className="doctor-reviewer-selector__avatar" src={doctor.avatar.url || doctor.avatar} alt="" />
      ) : (
        <i className="zmdi zmdi-account-circle doctor-reviewer-selector__avatar-icon" />
      )}
      <span className="doctor-reviewer-selector__result-name">
        {doctor.full_name || doctor.name}
      </span>
    </React.Fragment>
  );
}

// variant="dropdown" borrows HospitalSelector's shell (trigger, panel, search inside)
// via the shared `.search-select` classes; the search itself stays server-side either way.
export default function DoctorSelector({
  variant, multiple, selectedDoctors, onSelect, onRemove,
  searchDoctors, errors, translations
}) {
  var t = translations;
  var isDropdown = variant === 'dropdown';

  var queryState = useState('');
  var query = queryState[0];
  var setQuery = queryState[1];

  var resultsState = useState([]);
  var results = resultsState[0];
  var setResults = resultsState[1];

  var openState = useState(false);
  var isOpen = openState[0];
  var setIsOpen = openState[1];

  var searchingState = useState(false);
  var isSearching = searchingState[0];
  var setIsSearching = searchingState[1];

  var debounceRef = useRef(null);
  var containerRef = useRef(null);
  var triggerRef = useRef(null);
  var searchInputRef = useRef(null);

  var selected = selectedDoctors || [];
  var selectedIds = selected.map(function(d) { return d.id; });

  var performSearch = useCallback(function(searchQuery) {
    if (searchQuery && searchQuery.length < 2) {
      // Under two characters there is nothing worth asking the server for. The inline
      // form hides its list; the dropdown keeps the page it is already showing.
      if (!isDropdown) {
        setResults([]);
        setIsOpen(false);
      }
      return;
    }

    setIsSearching(true);
    searchDoctors(searchQuery || '')
      .then(function(doctors) {
        var filtered = multiple
          ? doctors.filter(function(d) { return selectedIds.indexOf(d.id) === -1; })
          : doctors;
        setResults(filtered);
        // The dropdown owns its open state: a search that comes back empty has to
        // leave the panel up, or the empty state never gets seen.
        if (!isDropdown) setIsOpen(filtered.length > 0);
      })
      .catch(function() {
        setResults([]);
      })
      .finally(function() {
        setIsSearching(false);
      });
  }, [searchDoctors, multiple, selectedIds, isDropdown]);

  var handleInputChange = useCallback(function(e) {
    var value = e.target.value;
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(function() { performSearch(value); }, 300);
  }, [performSearch]);

  var handleFocus = useCallback(function() {
    if (query.length >= 2 && results.length > 0) {
      setIsOpen(true);
    }
  }, [results, query]);

  var handleToggle = useCallback(function() {
    if (isOpen) {
      setIsOpen(false);
      return;
    }
    setQuery('');
    setIsOpen(true);
    performSearch('');
  }, [isOpen, performSearch]);

  var handleSelect = useCallback(function(doctor) {
    onSelect(doctor);
    setQuery('');
    setResults([]);
    setIsOpen(false);
  }, [onSelect]);

  var handleRemove = useCallback(function(index) {
    onRemove(index);
  }, [onRemove]);

  var handleClear = useCallback(function(e) {
    e.stopPropagation();
    onRemove(0);
    setIsOpen(false);
  }, [onRemove]);

  // Escape belongs to the open panel first -- the day modal listens for it too.
  var handleKeyDown = useCallback(function(e) {
    if (e.key !== 'Escape' || !isOpen) return;
    e.stopPropagation();
    setIsOpen(false);
    if (triggerRef.current) triggerRef.current.focus();
  }, [isOpen]);

  useEffect(function() {
    if (isDropdown && isOpen && searchInputRef.current) searchInputRef.current.focus();
  }, [isDropdown, isOpen]);

  useEffect(function() {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return function() { document.removeEventListener('mousedown', handleClickOutside); };
  }, []);

  useEffect(function() {
    return function() {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  var hasError = errors && errors.length > 0;
  var hasSingleSelection = !multiple && selected.length > 0;

  if (isDropdown) {
    var selectedDoctor = selected[0];
    var selectedName = selectedDoctor ? (selectedDoctor.full_name || selectedDoctor.name) : null;

    return (
      <div className="search-select" ref={containerRef} onKeyDown={handleKeyDown}>
        <button
          type="button"
          ref={triggerRef}
          className={'search-select__trigger' + (hasError ? ' search-select__trigger--error' : '')}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          onClick={handleToggle}
        >
          <span className={selectedName ? 'search-select__value' : 'search-select__placeholder'}>
            {selectedName || t.all_doctors}
          </span>
          <span className="search-select__icons">
            {/* Keeps the clear control's room inside the trigger; it is painted over this. */}
            {selectedName && <span className="search-select__clear-slot" aria-hidden="true" />}
            <i className={'zmdi ' + (isOpen ? 'zmdi-chevron-up' : 'zmdi-chevron-down')} aria-hidden="true" />
          </span>
        </button>

        {selectedName && (
          <button
            type="button"
            className="search-select__clear"
            aria-label={t.all_doctors}
            onClick={handleClear}
          >
            <i className="zmdi zmdi-close" aria-hidden="true" />
          </button>
        )}

        {hasError && (
          <div className="text-danger small mt-1">{errors.join(', ')}</div>
        )}

        {isOpen && (
          <div className="search-select__dropdown">
            <div className="search-select__search">
              <i className="zmdi zmdi-search" aria-hidden="true" />
              <input
                ref={searchInputRef}
                type="text"
                className="search-select__search-input"
                placeholder={t.search_doctor}
                value={query}
                onChange={handleInputChange}
                autoComplete="off"
              />
            </div>
            {/* Only options live in the listbox; the two status lines sit outside it. */}
            <div className="search-select__list" role="listbox" aria-label={t.search_doctor}>
              {results.map(function(doctor) {
                var isSelected = Boolean(selectedDoctor) && selectedDoctor.id === doctor.id;
                return (
                  <div
                    key={doctor.id}
                    role="option"
                    aria-selected={isSelected}
                    className={'search-select__item' + (isSelected ? ' search-select__item--selected' : '')}
                    onClick={function() { handleSelect(doctor); }}
                  >
                    <span className="doctor-reviewer-selector__selected-info">
                      <DoctorRow doctor={doctor} />
                      {doctor.speciality_name && (
                        <small className="doctor-reviewer-selector__result-specialty">
                          {doctor.speciality_name}
                        </small>
                      )}
                    </span>
                    {isSelected && <i className="zmdi zmdi-check" aria-hidden="true" />}
                  </div>
                );
              })}
            </div>

            {isSearching && <div className="search-select__empty">{t.loading}</div>}

            {!isSearching && results.length === 0 && (
              <div className="search-select__empty">{t.no_doctors_found}</div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="doctor-reviewer-selector" ref={containerRef}>
      {selected.length > 0 && (
        <div className={multiple ? 'doctor-reviewer-selector__multi-list mb-2' : ''}>
          {selected.map(function(doctor, index) {
            return (
              <div key={doctor.id} className="doctor-reviewer-selector__selected">
                <div className="doctor-reviewer-selector__selected-info">
                  {doctor.avatar ? (
                    <img className="doctor-reviewer-selector__avatar" src={doctor.avatar} alt="" />
                  ) : (
                    <i className="zmdi zmdi-account-circle" />
                  )}
                  <span>{doctor.name || 'Doctor #' + doctor.id}</span>
                </div>
                <button
                  type="button"
                  className="doctor-reviewer-selector__clear"
                  onClick={function() { handleRemove(index); }}
                  title="Remove"
                >
                  <i className="zmdi zmdi-close" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {!hasSingleSelection && (
        <div className="doctor-reviewer-selector__input form-group">
          <input
            type="text"
            className={'form-control' + (hasError ? ' is-invalid' : '')}
            placeholder={t.search_doctor}
            value={query}
            onChange={handleInputChange}
            onFocus={handleFocus}
            autoComplete="off"
          />
          <i className="form-group__bar" />
        </div>
      )}

      {hasError && (
        <div className="text-danger small mt-1">
          {errors.join(', ')}
        </div>
      )}

      {isOpen && results.length > 0 && (
        <div className="doctor-reviewer-selector__results">
          {results.map(function(doctor) {
            return (
              <div
                key={doctor.id}
                className="doctor-reviewer-selector__result-item"
                onClick={function() { handleSelect(doctor); }}
              >
                <DoctorRow doctor={doctor} />
              </div>
            );
          })}
        </div>
      )}

      {isSearching && query.length >= 2 && (
        <div className="doctor-reviewer-selector__results">
          <div className="doctor-reviewer-selector__empty">
            <span className="spinner-border spinner-border-sm me-2" />
            Loading...
          </div>
        </div>
      )}

      {isOpen && query.length >= 2 && results.length === 0 && !isSearching && (
        <div className="doctor-reviewer-selector__results">
          <div className="doctor-reviewer-selector__empty">{t.no_doctors_found}</div>
        </div>
      )}
    </div>
  );
}
